import { useEffect, useState } from 'react';
import { useTexts } from 'utils/useTexts';
import { AssetLinks } from '@assets/assets';
import { Button } from '@components/shared/Button/Button';
import { useFormContext } from '@context/FormContext';
import { Screens } from '@shared/constants/screens';
import { TextInput } from '@components/shared/form/TextInput';
import useForm, { validate } from '@shared/hooks/useForm';
import { PhoneTextInput } from '@components/shared/form/PhoneTextInput';
import { IVerifyYourIdentity, CDX_ACTIONS } from 'types/form';
import { useProve } from '@context/proveProvider';
import ProveClientSdk from '@services/proveClientSdk';
import { Flowtype, IProveStartPayload } from 'types/proveIdentity';
import { start } from '@services/prove';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';
import { createMask } from '../../utils/stringFormat';

const VehicleInfoConfirmForm = () => {
  const { t } = useTexts();
  const {
    currentStep,
    handleStep,
    licensePlateInfoResponse,
    licensePlateInfoForm,
    manualLicensePlateInfoForm,
    verifyYourIdentity,
    widgetInitVariables,
    updateVerifyYourIdentity,
    submitStart,
    isLoading,
    submitSuccess,
    dispatch,
  } = useFormContext();
  const { next } = useScreenNavigator();
  const { setAuthToken, setCorrelationId, setNextStep } = useProve();
  const { trackClick, trackPage ,startInteraction, trackFormSubmit} = useAnalytics(SEGMENT_SCREEN_PARAMS.PROVE_IDENTITY);
  const [isMobileFlow, setIsMobileFlow] = useState(false);
  const parsedUTM = JSON.parse(
    widgetInitVariables?.tracking?.extraData || '{}'
  );

  const desktopValidationSchema = {
    dateOfBirth: validate.string('dateOfBirth').required(t('verifyVehicleInfo.validation.dateOfBirth')).isAdult(),
    phone: validate
      .string('phone')
      .required(t('verifyVehicleInfo.validation.phone.required'))
      .matches(/^\d{10}$/, t('verifyVehicleInfo.validation.phone.validPhoneNumber')),
  };

  const mobileValidationSchema = {
    dateOfBirth: validate.string('dateOfBirth').required(t('verifyVehicleInfo.validation.dateOfBirth')).isAdult(),
    phone: validate.string('phone').matches(/^\d{10}$/, t('verifyVehicleInfo.validation.phone.validPhoneNumber')),
  };

  const validationSchema = isMobileFlow ? mobileValidationSchema : desktopValidationSchema;

  const { handleChange, errors, handleSubmit } = useForm<IVerifyYourIdentity>({
    initialValues: {
      ...verifyYourIdentity,
    },
    validationSchema,
    onSubmit: async () => {
      let requestPayload = {} as IProveStartPayload;
      if (isMobileFlow) {
        requestPayload = {
          flowType: Flowtype.MOBILE,
          dob: verifyYourIdentity.dateOfBirth,
        };
      } else {
        requestPayload = {
          flowType: Flowtype.DESKTOP,
          phoneNumber: `+1${verifyYourIdentity.phone}`,
          dob: verifyYourIdentity.dateOfBirth,
        };
      }
      submitStart();
      const startResponse = await start(requestPayload, parsedUTM?.anonymous_id);

      if ('error' in startResponse) {
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: startResponse,
        });
      } else {
        submitSuccess();
        setAuthToken(startResponse?.authToken);
        setCorrelationId(startResponse?.correlationId);
        setNextStep(startResponse?.next);
        next(currentStep, 'postSubmitOutcome', { isMobile: isMobileFlow }, false);
        
        //TODO;check why this was done for only non mobile flow
        trackFormSubmit('Form Submitted', {
          submission_status: true,
          verification_method: 'passive',
          forward_screen_name: 'contact_info',
        });
      }
    },
  });
  
  const handleInputChange = async <K extends keyof IVerifyYourIdentity>(field: K, value: IVerifyYourIdentity[K]) => {
    handleChange(field, value);
    updateVerifyYourIdentity({ [field]: value });
  };

  useEffect(() => {
    const proveAuthManager = new ProveClientSdk();
    const isMobile = proveAuthManager.authCheck();
    const fieldCount = isMobile ? 1 : 2;
    trackPage(t('pageTitles.VehicleInfoConfirm'), {
      verification_method:'passive',
      field_count: fieldCount,
      required_field_count: fieldCount,
    });
    setIsMobileFlow(isMobile);
  }, []);

  const handleEditVehicleInfoClick = () => {
    handleStep(Screens.ENTER_LICENSE_PLATE_MANUALLY);
    trackClick('Vehicle Info Edited', licensePlateInfoResponse);
  };
  
  return (
    <>
      <div className="iLendingCDX-vehicleInfo">
        <h1 className="iLendingCDX-form-title">{t('verifyVehicleInfo.title')}</h1>
        <div className="iLendingCDX-InfoCard iLendingCDX-mb-4">
          <div className="iLendingCDX-InfoCard-container iLendingCDX-mb-3 ">
            <div className="iLendingCDX-InfoCard-Icon ">
              <img
                src={AssetLinks.vehicleSide}
                alt=""
              />
            </div>
            <div className="iLendingCDX-InfoCard-info">
              <p>
                {licensePlateInfoResponse?.year || manualLicensePlateInfoForm?.year}{' '}
                {licensePlateInfoResponse?.make || manualLicensePlateInfoForm?.makeName}{' '}
                {licensePlateInfoResponse?.model || manualLicensePlateInfoForm?.model}
              </p>
              <p> {createMask('*', 3, licensePlateInfoResponse?.vin ?? '')}</p>
              <p>Estimated Mileage: {licensePlateInfoForm.estimatedMileageRange}</p>
            </div>
          </div>
          <span
            className="iLendingCDX-InfoCard-edit"
            onClick={handleEditVehicleInfoClick}
          >
            <img
              src={AssetLinks.pencil}
              alt=""
            />
            {t('verifyVehicleInfo.editVehicleInfo')}
          </span>
        </div>
      </div>

      <h1 className="iLendingCDX-form-title">{t('verifyVehicleInfo.title2')}</h1>
      <p className={'iLendingCDX-mb-8'}>{t('verifyVehicleInfo.weCanPrefill')}</p>
      <form onSubmit={handleSubmit} onFocus={startInteraction}>
        <TextInput
          label={t('verifyVehicleInfo.form.dateOfBirth')}
          className={'iLendingCDX-mb-4'}
          name="dateOfBirth"
          type="date"
          max="9999-12-31"
          value={verifyYourIdentity?.dateOfBirth}
          errorMessage={errors.dateOfBirth}
          onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
        />

        {!isMobileFlow && (
          <PhoneTextInput
            label={t('verifyVehicleInfo.form.phone')}
            className={'iLendingCDX-mb-4'}
            name="phone"
            value={verifyYourIdentity?.phone}
            errorMessage={errors.phone}
            onChange={(value) => handleInputChange('phone', value as IVerifyYourIdentity['phone'])}
          />
        )}

        {isMobileFlow ? (
          <p className="iLendingCDX-para iLendingCDX-mb-4">{t('verifyVehicleInfo.termsAndConditionsText')}</p>
        ) : (
          <p className="iLendingCDX-para iLendingCDX-mb-4">{t('verifyVehicleInfo.termsAndConditionsText1')}</p>
        )}

        <p className="iLendingCDX-para iLendingCDX-mb-4 iLendingCDX-input-label--check">
          {t('verifyVehicleInfo.byClickingContinue')}
          <a
            href="https://ilendingcarloanrefinancing.com/privacy-policy/"
            target="_blank"
            rel="noreferrer"
          >
            {t('verifyVehicleInfo.termsAndConditionsText2')}
            {'.'}
          </a>
        </p>

        <Button
          type="submit"
          className="iLendingCDX-button--rounded"
          size="lg"
          isLoading={isLoading}
        >
          {t('verifyVehicleInfo.form.continue')}
        </Button>
      </form>
    </>
  );
};

export default VehicleInfoConfirmForm;
